import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from "@mui/material";
import {
    Construction,
    ArrowDropDown,
    LocalShipping,
    Payment,
    PriceCheck,
} from "@mui/icons-material";
import { usePermissions } from "../../hooks/usePermissions";

// Ids mirror the tool ids in store/slices/toolsSlice.ts's loadTools thunk —
// keep these in sync if a tool's route or name ever changes there. Kept as
// a small standalone registry (not imported from that Redux slice) so this
// component works instantly from any page without depending on the tools
// slice having been loaded — real icon components too, unlike ToolCard.tsx's
// string-keyed icon lookup (which has its own unrelated pre-existing gap).
const TOOL_LINKS: Record<string, { label: string; path: string; icon: React.ElementType }> = {
    "delivery-planner": {
        label: "Delivery Planner",
        path: "/tools/delivery-planner",
        icon: LocalShipping,
    },
    "daily-cash-flow-analyzer": {
        label: "Cash Flow Analyzer",
        path: "/tools/daily-cash-flow-analyzer",
        icon: Payment,
    },
    "price-list-generator": {
        label: "Price List Generator",
        path: "/tools/price-list-generator",
        icon: PriceCheck,
    },
};

interface QuickToolLinksProps {
    /** Tool ids from TOOL_LINKS above, relevant to the page this is dropped into. */
    toolIds: string[];
}

// A page-header quick-link to related Tools pages — always renders as
// exactly ONE button no matter how many tools are passed in (a direct link
// for one tool, a dropdown menu for two or more), so a page's header never
// grows a wall of buttons as more tools become relevant to it over time.
// Hidden entirely for Investor logins, since /tools is Operations-gated and
// Investors can't reach it — see RequireArea usage in App.tsx and
// Sidebar.tsx's INVESTOR_HIDDEN_ITEM_IDS, which this mirrors.
export const QuickToolLinks: React.FC<QuickToolLinksProps> = ({ toolIds }) => {
    const navigate = useNavigate();
    const { canAccessArea } = usePermissions();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const tools = toolIds
        .map((id) => TOOL_LINKS[id])
        .filter((t): t is (typeof TOOL_LINKS)[string] => Boolean(t));

    if (tools.length === 0 || !canAccessArea("operations")) {
        return null;
    }

    if (tools.length === 1) {
        const { label, path, icon: Icon } = tools[0];
        return (
            <Tooltip title={`Open ${label}`}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon />}
                    onClick={() => navigate(path)}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    {label}
                </Button>
            </Tooltip>
        );
    }

    return (
        <>
            <Tooltip title="Related tools">
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Construction />}
                    endIcon={<ArrowDropDown />}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    Tools
                </Button>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                {tools.map(({ label, path, icon: Icon }) => (
                    <MenuItem
                        key={path}
                        onClick={() => {
                            setAnchorEl(null);
                            navigate(path);
                        }}
                    >
                        <ListItemIcon>
                            <Icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
